/**
 * @file status-meta.ts
 * @description Реестр статусов кабинета магазина (phase19.31, шаг О4;
 * SC25–SC29, uiux-design.md §6).
 *
 * Шесть тонов на весь кабинет — пилюля с точкой и текстом
 * (commora-console.md §8):
 *  - `success` — успех: проведено, закрыто, в наличии (зелёный);
 *  - `progress` — в работе: новое задание, идёт сборка (синий);
 *  - `attention` — ждёт решения, скоро истекает, мало (янтарный);
 *  - `error` — ошибка, недостача, «отменено» (красный, SC26);
 *  - `special` — особое (фиолетовый, в магазине не применяется);
 *  - `neutral` — черновик, архив, неактивно (серый).
 *
 * Подпись и тон каждой сущности — записью здесь, а не цветом в разметке:
 * «Проведено» выглядит одинаково в реестре, в документе и на сводке.
 * Цвета тонов — в теме (`console-rules.css`), в шаблон темы не входят (SC27).
 * Начат со склада (зона «Склад», О4); заказы, отзывы и сделки переезжают
 * сюда при переводе своих зон.
 */

import { PICKING_STATUS_LABEL, type PickingTaskStatus } from "../api-picking";

/** Тон статуса — один из шести. */
export type StatusTone = "success" | "progress" | "attention" | "error" | "special" | "neutral";

/** Вариант пилюли `Badge` для тона (имена вариантов — из кабинета платформы). */
export type StatusBadgeVariant = "success" | "info" | "warning" | "destructive" | "special" | "neutral";

export const STATUS_TONE_VARIANT: Record<StatusTone, StatusBadgeVariant> = {
  success: "success",
  progress: "info",
  attention: "warning",
  error: "destructive",
  special: "special",
  neutral: "neutral",
};

/** Подпись и тон одного статуса. */
export interface ShopStatusMeta {
  label: string;
  tone: StatusTone;
}

/**
 * Тон документа (`DocumentStatusBadge`, phase19.24) → тон кабинета.
 * `done` — зелёный, а не цвет основной кнопки; `cancelled` — красный (SC26).
 */
export type DocumentStatusTone = "draft" | "active" | "attention" | "danger" | "done" | "cancelled";

export const DOCUMENT_TONE_STATUS: Record<DocumentStatusTone, StatusTone> = {
  draft: "neutral",
  active: "progress",
  attention: "attention",
  danger: "error",
  done: "success",
  cancelled: "error",
};

/** Складские документы: приёмка, перемещение, списание. */
export type WmsDocumentStatus = "DRAFT" | "COMMITTED" | "CANCELLED";

export const WMS_DOCUMENT_STATUS_META: Record<WmsDocumentStatus, ShopStatusMeta> = {
  DRAFT: { label: "Черновик", tone: "neutral" },
  COMMITTED: { label: "Проведено", tone: "success" },
  CANCELLED: { label: "Отменено", tone: "error" },
};

/** Приёмка — «она»: «Проведена», а не «Проведено». */
export const RECEIPT_STATUS_META: Record<Exclude<WmsDocumentStatus, "CANCELLED">, ShopStatusMeta> = {
  DRAFT: { label: "Черновик", tone: "neutral" },
  COMMITTED: { label: "Проведена", tone: "success" },
};

/**
 * Задание на сборку. Подписи — прежние (`PICKING_STATUS_LABEL`: «Закрыто»,
 * а не «Собрано» — слово занято прогрессом «собрано 2 из 3»).
 */
export const PICKING_STATUS_META: Record<PickingTaskStatus, ShopStatusMeta> = {
  NEW: { label: PICKING_STATUS_LABEL.NEW, tone: "progress" },
  IN_PROGRESS: { label: PICKING_STATUS_LABEL.IN_PROGRESS, tone: "progress" },
  DONE: { label: PICKING_STATUS_LABEL.DONE, tone: "success" },
  SHORTAGE: { label: PICKING_STATUS_LABEL.SHORTAGE, tone: "error" },
  CANCELLED: { label: PICKING_STATUS_LABEL.CANCELLED, tone: "error" },
};

/** Срок годности партии. */
export type ExpiryState = "soon" | "expired";

export const EXPIRY_STATUS_META: Record<ExpiryState, ShopStatusMeta> = {
  soon: { label: "Скоро истекает", tone: "attention" },
  expired: { label: "Истёк", tone: "error" },
};

/** Остаток фасовки. */
export type StockLevelState = "in_stock" | "low" | "out";

export const STOCK_LEVEL_META: Record<StockLevelState, ShopStatusMeta> = {
  in_stock: { label: "В наличии", tone: "success" },
  low: { label: "Мало", tone: "attention" },
  out: { label: "Нет", tone: "error" },
};
